import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

export default function Recipes() {
  const { t } = useLang();
  const [recipes, setRecipes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");
  const [sentMsg, setSentMsg] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/recipes");
    setRecipes(data.recipes);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toShopping = async (id) => {
    try {
      const { data } = await api.post(`/recipes/${id}/to-shopping`);
      setSentMsg(`+${data.added}`);
      setTimeout(() => setSentMsg(""), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const removeRecipe = async (id) => {
    await api.delete(`/recipes/${id}`);
    load();
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-5xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-cap">— {t("app.sidebar2.recipes")}</p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="recipes-title">
            {t("app.recipesTitle")}
          </h1>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-outline" data-testid="recipes-new-btn">
          + {t("app.recipesNew")}
        </button>
      </div>

      {sentMsg && <p className="label-cap text-[var(--success)] mt-4">{sentMsg} → {t("app.shoppingTitle")}</p>}
      {error && <p className="text-[var(--error)] text-sm mt-4">{error}</p>}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] mt-10">
        {recipes.length === 0 && (
          <p className="bg-[var(--bg)] p-10 font-display-italic text-[var(--ink-soft)] md:col-span-3" data-testid="recipes-empty">
            {t("app.recipesEmpty")}
          </p>
        )}
        {recipes.map((r) => (
          <article key={r.id} className="bg-[var(--bg)] p-8 flex flex-col" data-testid={`recipe-${r.id}`}>
            <h3 className="font-display text-2xl">{r.name}</h3>
            <ul className="mt-4 space-y-1 text-sm text-[var(--ink-soft)] flex-1">
              {r.ingredients.slice(0, 8).map((ing, i) => (
                <li key={`${ing}-${i}`}>· {ing}</li>
              ))}
              {r.ingredients.length > 8 && <li className="italic">+{r.ingredients.length - 8}…</li>}
            </ul>
            {r.steps && (
              <p className="text-xs text-[var(--ink-faint)] mt-3 italic line-clamp-3">{r.steps}</p>
            )}
            <div className="mt-6 flex justify-between gap-3">
              <button
                onClick={() => toShopping(r.id)}
                className="btn-primary"
                data-testid={`recipe-to-shopping-${r.id}`}
              >
                ⤓ {t("app.recipesToShopping")}
              </button>
              <button
                onClick={() => removeRecipe(r.id)}
                className="btn-ghost"
                data-testid={`recipe-delete-${r.id}`}
              >
                ×
              </button>
            </div>
          </article>
        ))}
      </div>

      {showNew && <NewRecipeDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewRecipeDialog({ onClose, onCreated }) {
  const { t } = useLang();
  const [name, setName] = useState("");
  const [ingredients, setIngredients] = useState("");
  const [steps, setSteps] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const arr = ingredients
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post("/recipes", { name, ingredients: arr, steps });
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-[var(--ink)]/40 flex items-center justify-center px-4" onClick={onClose} data-testid="new-recipe-dialog">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-[var(--surface)] border border-[var(--border)] p-10 w-full max-w-lg"
      >
        <p className="label-cap">— {t("app.recipesNew")}</p>
        <h2 className="font-display text-3xl font-light tracking-tight mt-4">{t("app.recipesName")}</h2>
        <div className="mt-6 space-y-5">
          <input className="line-input" required placeholder={t("app.recipesName")} value={name} onChange={(e) => setName(e.target.value)} data-testid="new-recipe-name" />
          <label className="block">
            <span className="label-cap">{t("app.recipesIngredients")}</span>
            <textarea
              rows={6}
              className="line-input resize-none"
              required
              value={ingredients}
              onChange={(e) => setIngredients(e.target.value)}
              data-testid="new-recipe-ingredients"
              placeholder="100g feijão&#10;1 cebola&#10;2 dentes de alho"
            />
          </label>
          <label className="block">
            <span className="label-cap">{t("app.recipesSteps")}</span>
            <textarea rows={3} className="line-input resize-none" value={steps} onChange={(e) => setSteps(e.target.value)} data-testid="new-recipe-steps" />
          </label>
        </div>
        <div className="flex justify-end gap-4 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost">{t("app.cancel")}</button>
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="new-recipe-submit">
            {submitting ? "…" : t("app.create")}
          </button>
        </div>
      </form>
    </div>
  );
}
